import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Temporal } from "@js-temporal/polyfill";
import { useNavigate } from "react-router";
import type { Pot } from "@/types";
import { PlantAvatarGroup } from "@/app/components/PlantAvatarGroup";
import { BackIcon, PlusIcon, SparkleIcon, SunIcon } from "@/app/components/system/Icons";
import { Button } from "@/app/components/system/Button";
import { PotIllustration } from "@/app/components/PotIllustration";
import { PotCover } from "@/app/components/PotCover";
import { PotWateringRecommendations } from "@/app/components/PotWateringRecommendations";
import { SunHoursModal } from "@/app/components/SunHoursModal";
import { WeatherHero, summarizeDay } from "@/app/components/weather/WeatherHero";
import { WeatherBackground } from "@/app/components/weather/WeatherBackground";
import { useWeatherBackdrop } from "@/app/components/weather/WeatherBanner";
import { UserMenu } from "@/app/components/UserMenu";
import { WeatherGlyph } from "@/app/components/weather/WeatherGlyph";
import { TempBar, computeTempScale } from "@/app/components/weather/TempBar";
import { potSizeShortLabel } from "@/app/lib/potSizes";
import { usePots, useWeather } from "../lib/data-context";

/** Sort rank by pot size, largest first. */
const POT_SIZE_RANK: Record<Pot["size"], number> = { large: 0, medium: 1, small: 2 };

export function LibraryScreen() {
  const navigate = useNavigate();
  const { pots } = usePots();
  const { dark: illustratedDark } = useWeatherBackdrop();
  const libraryRef = useRef<HTMLDivElement>(null);
  const [showDayOutlook, setShowDayOutlook] = useState(true);

  const plants = useMemo(() => pots.map(pot => pot.plants).flat(), [pots]);

  // Order pots by size (largest first), then plant count (most first), then name (A→Z).
  const sortedPots = useMemo(
    () =>
      [...pots].sort(
        (a, b) =>
          POT_SIZE_RANK[a.size] - POT_SIZE_RANK[b.size] ||
          b.plants.length - a.plants.length ||
          a.name.localeCompare(b.name),
      ),
    [pots],
  );

  useEffect(() => {
    const root = libraryRef.current;
    if (!root) return;

    /** Scroll down/up on Library collapses or restores the multi-day outlook. */
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY > 4) setShowDayOutlook(false);
      else if (e.deltaY < -4) setShowDayOutlook(true);
    };

    root.addEventListener("wheel", onWheel, { passive: true });
    return () => root.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div
      ref={libraryRef}
      className="relative flex-1 min-h-0 h-full flex flex-col overflow-hidden"
    >
      <WeatherBackground />

      <div className="relative z-10 flex flex-col flex-1 min-h-0 h-full">
        <div className="shrink-0 px-5.5 pt-6 pb-3">
          <LibraryHeader showAdd={pots.length > 0} illustratedDark={illustratedDark} />
          <WeatherHero showDayOutlook={showDayOutlook} illustratedDark={illustratedDark} />
        </div>

        {pots.length === 0 ? (
          <div className="flex-1 min-h-0 flex items-center justify-center px-4 pb-4">
            <EmptyGarden onAdd={() => navigate("/add")} />
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <div
              role="region"
              aria-label="Pots"
              className="flex flex-col flex-1 min-h-0 gap-3.5 px-4 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch] pb-6"
            >
              {sortedPots.map((pot) => (
                <PotSection
                  key={pot.id}
                  pot={pot}
                  onPotClick={() => navigate(`/pots/${pot.id}`)}
                  onPlantClick={(id) => navigate(`/plants/${id}`)}
                  onAdd={() => navigate(`/add?potId=${pot.id}`)}
                />
              ))}
            </div>
            <GardenStatsFooter
              potCount={sortedPots.length}
              plantCount={plants.length}
              illustratedDark={illustratedDark}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Plant and pot count below the pot list. */
function GardenStatsFooter({ potCount, plantCount, illustratedDark = false }: Readonly<{
  potCount: number;
  plantCount: number;
  illustratedDark?: boolean;
}>) {
  return (
    <footer className="shrink-0 flex flex-col items-center px-4 pt-4 pb-2">
      <p className={`m-0 font-sans text-[11px] font-medium text-center ${illustratedDark ? "text-white/60" : "text-ink-3"}`}>
        <strong className={illustratedDark ? "text-white/90 font-semibold" : "text-ink font-semibold"}>{plantCount}</strong>{" "}
        {plantCount === 1 ? "plant" : "plants"} in{" "}
        <strong className={illustratedDark ? "text-white/90 font-semibold" : "text-ink font-semibold"}>{potCount}</strong>{" "}
        {potCount === 1 ? "pot" : "pots"}
      </p>
    </footer>
  );
}

/** Glass header control matching {@link UserMenu} trigger styling. */
function GlassHeaderButton({ dark, ariaLabel, onClick, label, children }: Readonly<{
  dark: boolean;
  ariaLabel: string;
  onClick: () => void;
  /** When set, renders a pill with icon + label instead of a square icon button. */
  label?: string;
  children: ReactNode;
}>) {
  const glass = dark ? "text-paper hover:bg-white/15" : "text-ink hover:bg-white/85";
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-full cursor-pointer border backdrop-blur-md transition-[background,transform] duration-150 active:scale-[0.96] ${glass} ${label ? "gap-1.5 h-8 pl-2.5 pr-3" : "w-8 h-8"}`}
      style={{
        background: dark ? "oklch(1 0 0 / 0.14)" : "oklch(1 0 0 / 0.65)",
        borderColor: dark ? "oklch(1 0 0 / 0.22)" : "oklch(1 0 0 / 0.55)",
      }}
    >
      {children}
      {label ? (
        <span className="font-sans text-[13px] font-semibold whitespace-nowrap">{label}</span>
      ) : null}
    </button>
  );
}

function LibraryHeader({ showAdd, illustratedDark }: Readonly<{ showAdd: boolean; illustratedDark?: boolean }>) {
  const navigate = useNavigate();
  const dark = illustratedDark ?? false;
  const { weather } = useWeather();
  const todaySummary = weather?.today ? summarizeDay(weather.today) : null;
  const currentTempC = weather?.today.hours[Temporal.Now.zonedDateTimeISO().hour]?.tempC;
  const scale = weather
    ? computeTempScale([
      summarizeDay(weather.today),
      summarizeDay(weather.oneDayLater),
      summarizeDay(weather.twoDaysLater),
    ])
    : null;
  return (
    <header className="flex flex-col gap-1.5 mb-5">
      <div className="flex items-center justify-between gap-3">
        <p className={`text-[11px] tracking-[0.12em] uppercase m-0 font-medium inline-flex items-center gap-1.5 ${dark ? "text-white/45" : "text-ink-3"}`}>
          {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {showAdd && (
            <GlassHeaderButton dark={dark} ariaLabel="Add plant" label="Add" onClick={() => navigate("/add")}>
              <PlusIcon size={16} stroke={2} />
            </GlassHeaderButton>
          )}
          <UserMenu dark={dark} />
        </div>
      </div>
      <div className="flex items-end justify-between gap-3">
        <h1 className={`font-display font-medium text-[30px] leading-[1.05] tracking-[-0.02em] m-0 ${dark ? "text-paper" : "text-ink"}`}>
          My garden
        </h1>
        {todaySummary && scale && (
          <div className="inline-flex items-center gap-2 pb-1.5">
            <span className="grid place-items-center">
              <WeatherGlyph condition={todaySummary.condition} size={18} />
            </span>
            <span className={`font-display font-semibold text-[12px] tracking-tight w-7 text-right ${dark ? "text-white/50" : "text-ink-3"}`}>
              {Math.round(todaySummary.tempMinC)}°
            </span>
            <TempBar
              dayMin={todaySummary.tempMinC}
              dayMax={todaySummary.tempMaxC}
              globalMin={scale.globalMin}
              globalMax={scale.globalMax}
              current={currentTempC}
              className="w-20"
            />
            <span className={`font-display font-semibold text-[13px] tracking-tight w-7 text-left ${dark ? "text-paper" : "text-ink"}`}>
              {Math.round(todaySummary.tempMaxC)}°
            </span>
          </div>
        )}
      </div>
    </header>
  );
}

interface EmptyGardenProps { onAdd: () => void }
function EmptyGarden({ onAdd }: EmptyGardenProps) {
  return (
    <div className="flex flex-col items-center text-center gap-3 px-5 pt-9 pb-7 w-full max-w-sm bg-cream-2 border border-dashed border-line rounded-[22px]">
      <div className="relative w-35 h-35 grid place-items-center mb-1 *:first:w-full *:first:h-full *:first:block" aria-hidden="true">
        <PotIllustration shape="terracotta" plants={[]} size="large" />
        <span className="absolute top-1.5 right-3.5 w-8 h-8 rounded-full bg-paper border border-line grid place-items-center text-moss">
          <SparkleIcon size={18} />
        </span>
      </div>
      <h2 className="m-0 font-display font-medium text-[22px] text-ink">Your garden starts here</h2>
      <p className="m-0 mb-1.5 max-w-[32ch] font-sans text-[13.5px] text-ink-2 leading-[1.45]">
        Scan a label, or set up a pot to group plants by where they live.
      </p>
      <Button onClick={onAdd} className="mt-1">
        <PlusIcon size={16} stroke={2} /> Get started
      </Button>
    </div>
  );
}

function PotSection({ pot, onPotClick, onPlantClick, onAdd }: Readonly<{
  pot: Pot;
  onPotClick: () => void;
  onPlantClick: (id: string) => void;
  onAdd: () => void;
}>) {
  const isEmpty = pot.plants.length === 0;
  const sectionBg = isEmpty ? "bg-cream-2" : "bg-paper";
  const hasSunHours = !!(pot.sunHours?.start && pot.sunHours?.end);
  const [sunOpen, setSunOpen] = useState(false);

  return (
    <article className={`${sectionBg} w-full shrink-0 flex flex-col border border-line rounded-[20px] pt-3.5 pb-2.5 px-3.5 transition-[border-color] duration-150 hover:border-moss-2`}>
      <header className="shrink-0 grid items-center gap-3 grid-cols-[auto_1fr_auto]">
        <button
          type="button"
          onClick={onPotClick}
          aria-label={`Open ${pot.name}`}
          className="bg-transparent border-0 p-0 cursor-pointer grid place-items-center rounded-[14px] transition-[background] duration-150 hover:bg-cream-2"
        >
          <PotCover pot={pot} />
        </button>
        <div className="min-w-0 flex flex-col items-start">
          <button
            type="button"
            onClick={onPotClick}
            className="bg-transparent w-full border-0 p-0 inline-flex items-center gap-1.5 cursor-pointer text-ink text-left [&>svg]:text-ink-3 [&>svg]:transition-transform [&>svg]:duration-150 hover:[&>svg]:text-ink hover:[&>svg]:translate-x-0.5"
          >
            <div className="flex-1">
              <p className="text-ink-3 text-xs m-0">{potSizeShortLabel(pot.size)}</p>
              <h2 className="m-0 text-lg tracking-tight font-display font-medium">{pot.name}</h2>
            </div>
            <BackIcon size={14} stroke={2} />
          </button>
          {hasSunHours ? (
            <span className="flex items-center gap-px text-ink-2 text-xs">
              <SunIcon />
              {pot.sunHours?.start}-{pot.sunHours?.end}
            </span>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSunOpen(true); }}
              className="inline-flex items-center gap-1.5 text-xs text-ink-2 hover:text-ink bg-transparent hover:bg-cream-2 border border-line hover:border-moss-2 rounded-full cursor-pointer px-2 py-0.75 mt-1 transition-colors duration-150"
            >
              <SunIcon />
              <span>Set sun hours</span>
            </button>
          )}
          {pot.plants.length > 1 && (
            <PlantAvatarGroup plants={pot.plants} onPlantClick={onPlantClick} className="mt-1.5" />
          )}
        </div>
      </header>
      <div className="mt-4">
        {isEmpty ? (
          <p className="inline-flex items-center gap-1 text-xs text-ink-3 m-0">Empty · ready for a plant</p>
        ) : (
          <PotWateringRecommendations pot={pot} />
        )}
        {isEmpty && (
          <button
            type="button"
            onClick={onAdd}
            className="flex items-center justify-center gap-2 w-full mt-2 px-3.5 py-3 border-[1.5px] border-dashed border-line rounded-[14px] bg-transparent text-moss font-sans text-[13.5px] font-medium cursor-pointer transition-[border-color,background,color] duration-150 hover:border-moss hover:bg-moss-soft"
          >
            <span className="w-5.5 h-5.5 rounded-full grid place-items-center bg-moss-soft text-moss">
              <PlusIcon size={14} stroke={2} />
            </span>
            <span>Add a plant</span>
          </button>
        )}
      </div>
      {sunOpen && <SunHoursModal pot={pot} onClose={() => setSunOpen(false)} />}
    </article>
  );
}
